import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth, PORTAL_PATH } from "@/lib/AuthContext";
import { AuthShell, Field, FormError, inputClass, SubmitButton } from "@/pages/auth/Login";

export default function ResetPassword({ invite = false }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError("This link is missing its token. Ask AnyTribe to send a new one.");
        setChecking(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/token-info", { params: { token } });
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) setError(apiError(e));
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Both passwords must match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data.user);
      navigate(PORTAL_PATH[data.user.role] || "/", { replace: true });
    } catch (e) {
      setError(apiError(e));
      setBusy(false);
    }
  };

  const title = invite ? "Activate your account" : "Set a new password";

  return (
    <AuthShell
      title={title}
      subtitle={info ? `For ${info.email}` : undefined}
      testId={invite ? "accept-invite-page" : "reset-password-page"}
    >
      {checking ? (
        <p className="text-sm body-text">Checking your link…</p>
      ) : info ? (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
          <FormError message={error} />
          <Field label="New password" hint="At least 8 characters.">
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              data-testid="reset-password-input"
            />
          </Field>
          <Field label="Confirm password">
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
              data-testid="reset-confirm-input"
            />
          </Field>
          <SubmitButton busy={busy} testId="reset-submit-button">
            {invite ? "Activate account" : "Update password"}
          </SubmitButton>
        </form>
      ) : (
        <div className="space-y-5">
          <FormError message={error} />
          <p className="text-sm body-text" data-testid="ask-admin-note">
            Ask your AnyTribe administrator to send you a new link.
          </p>
        </div>
      )}
    </AuthShell>
  );
}
